import React, { useState } from 'react';
import { User, Lock, Eye, EyeOff, Check, X, Award } from 'lucide-react';

const UserProfile = ({ currentUser, apiUrl, token, onLogout }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const roleConfig = {
    admin: { label: 'Administrateur', color: '#ef4444', description: 'Accès complet' },
    editor: { label: 'Éditeur', color: '#f59e0b', description: 'Lecture + Édition + Validation' },
    reader: { label: 'Lecteur', color: '#6b7280', description: 'Lecture seule' }
  };

  const authenticatedFetch = async (url, options = {}) => {
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'Le mot de passe actuel est requis';
    }

    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'Le nouveau mot de passe est requis';
    } else if (passwordForm.newPassword.length < 6) {
      newErrors.newPassword = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      newErrors.newPassword = 'Le nouveau mot de passe doit être différent de l\'ancien';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setSuccess('');
    setErrors({});

    if (!validatePasswordForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await authenticatedFetch(`${apiUrl}/auth/change-password`, {
        method: 'POST',
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      if (response.ok) {
        setSuccess('✅ Mot de passe modifié avec succès');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // Auto-déconnexion après 2 secondes
        setTimeout(() => {
          alert('Veuillez vous reconnecter avec votre nouveau mot de passe');
          onLogout();
        }, 2000);
      } else {
        const error = await response.json();
        setErrors({ general: error.error || 'Erreur lors du changement de mot de passe' });
      }
    } catch (error) {
      setErrors({ general: 'Erreur de connexion au serveur' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Jamais';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
      {/* En-tête du profil */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        marginBottom: '24px',
        padding: '32px',
        textAlign: 'center'
      }}>
        <div style={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          backgroundColor: roleConfig[currentUser.role]?.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '40px',
          fontWeight: '600',
          margin: '0 auto 20px'
        }}>
          {currentUser.name.charAt(0).toUpperCase()}
        </div>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>
          {currentUser.name}
        </h1>
        <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '16px' }}>
          {currentUser.email}
        </p>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 20px',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '600',
          color: 'white',
          backgroundColor: roleConfig[currentUser.role]?.color
        }}>
          {roleConfig[currentUser.role]?.label}
        </span>
      </div>

      {/* Navigation par onglets */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <button
          onClick={() => setActiveTab('profile')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'profile' ? '3px solid #3b82f6' : 'none',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            color: activeTab === 'profile' ? '#3b82f6' : '#6b7280',
            transition: 'all 0.3s'
          }}
        >
          <User size={18} style={{ display: 'inline', marginRight: '8px' }} />
          Mon profil
        </button>
        <button
          onClick={() => setActiveTab('password')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'password' ? '3px solid #3b82f6' : 'none',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            color: activeTab === 'password' ? '#3b82f6' : '#6b7280',
            transition: 'all 0.3s'
          }}
        >
          <Lock size={18} style={{ display: 'inline', marginRight: '8px' }} />
          Mot de passe
        </button>
        {currentUser.points && (
          <button
            onClick={() => setActiveTab('points')}
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'points' ? '3px solid #3b82f6' : 'none',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              color: activeTab === 'points' ? '#3b82f6' : '#6b7280',
              transition: 'all 0.3s'
            }}
          >
            <Award size={18} style={{ display: 'inline', marginRight: '8px' }} />
            Mes points
          </button>
        )}
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'profile' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          padding: '32px'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '24px' }}>
            Informations du compte
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div className="detail-item">
              <label>Nom complet</label>
              <p style={{ fontSize: '16px', color: '#111827', fontWeight: '500' }}>
                {currentUser.name}
              </p>
            </div>

            <div className="detail-item">
              <label>Adresse email</label>
              <p style={{ fontSize: '16px', color: '#111827', fontWeight: '500' }}>
                {currentUser.email}
              </p>
            </div>

            <div className="detail-item">
              <label>Rôle</label>
              <p style={{ fontSize: '16px', color: '#111827', fontWeight: '500' }}>
                {roleConfig[currentUser.role]?.label}
              </p>
              <span style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                {roleConfig[currentUser.role]?.description}
              </span>
            </div>

            <div className="detail-item">
              <label>Dernière connexion</label>
              <p style={{ fontSize: '16px', color: '#6b7280' }}>
                {formatDate(currentUser.lastLogin)}
              </p>
            </div>

            <div className="detail-item">
              <label>Membre depuis</label>
              <p style={{ fontSize: '16px', color: '#6b7280' }}>
                {formatDate(currentUser.createdAt)}
              </p>
            </div>
          </div>

          <div style={{
            marginTop: '32px',
            padding: '16px',
            backgroundColor: '#eff6ff',
            borderRadius: '8px',
            border: '1px solid #3b82f6'
          }}>
            <p style={{ fontSize: '13px', color: '#1e40af', margin: 0 }}>
              💡 <strong>Note :</strong> Pour modifier vos informations personnelles, veuillez contacter un administrateur.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'password' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          padding: '32px'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
            Changer le mot de passe
          </h2>
          <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>
            Pour votre sécurité, choisissez un mot de passe fort et unique.
          </p>

          {errors.general && (
            <div style={{
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {errors.general}
            </div>
          )}

          {success && (
            <div style={{
              backgroundColor: '#d1fae5',
              border: '1px solid #a7f3d0',
              color: '#065f46',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {success}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div className="form-group">
                <label>Mot de passe actuel *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="inline-edit-input"
                    placeholder="Entrez votre mot de passe actuel"
                    style={{
                      paddingRight: '40px',
                      borderColor: errors.currentPassword ? '#ef4444' : undefined
                    }}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.currentPassword && (
                  <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {errors.currentPassword}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>Nouveau mot de passe *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="inline-edit-input"
                    placeholder="Minimum 6 caractères"
                    style={{
                      paddingRight: '40px',
                      borderColor: errors.newPassword ? '#ef4444' : undefined
                    }}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.newPassword && (
                  <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {errors.newPassword}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>Confirmer le nouveau mot de passe *</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="inline-edit-input"
                  placeholder="Retapez le nouveau mot de passe"
                  style={{ borderColor: errors.confirmPassword ? '#ef4444' : undefined }}
                  disabled={loading}
                />
                {errors.confirmPassword && (
                  <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {errors.confirmPassword}
                  </span>
                )}
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                marginTop: '12px',
                paddingTop: '20px',
                borderTop: '1px solid #e5e7eb'
              }}>
                <button 
                  onClick={handleChangePassword} 
                  className="btn btn-primary" 
                  style={{ flex: 1 }}
                  disabled={loading}
                >
                  <Check className="icon" />
                  {loading ? 'Enregistrement...' : 'Changer le mot de passe'}
                </button>
                <button
                  onClick={() => {
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setErrors({});
                    setSuccess('');
                  }}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  <X className="icon" />
                  Annuler
                </button>
              </div>
            </div>

          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
            border: '1px solid #fbbf24'
          }}>
            <p style={{ fontSize: '13px', color: '#92400e', margin: 0, marginBottom: '8px' }}>
              <strong>🔒 Conseils de sécurité :</strong>
            </p>
            <ul style={{ fontSize: '13px', color: '#92400e', margin: 0, paddingLeft: '20px' }}>
              <li>Utilisez au moins 6 caractères</li>
              <li>Mélangez lettres, chiffres et symboles</li>
              <li>Évitez les mots du dictionnaire</li>
              <li>Ne réutilisez pas vos anciens mots de passe</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'points' && currentUser.points && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          padding: '32px'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '24px' }}>
            Mon système de points
          </h2>

          <div style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            border: '2px solid #fbbf24'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px'
            }}>
              <div>
                <div style={{ fontSize: '14px', color: '#92400e', marginBottom: '8px' }}>
                  Points totaux
                </div>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#78350f' }}>
                  {currentUser.points.total}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '14px', color: '#92400e', marginBottom: '8px' }}>
                  Points en attente
                </div>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#f59e0b' }}>
                  {currentUser.points.pending}
                </div>
              </div>
            </div>
          </div>

          {currentUser.stats && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                  Total
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>
                  {currentUser.stats.totalEdits}
                </div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                  Validées
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                  {currentUser.stats.validatedEdits}
                </div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                  En attente
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>
                  {currentUser.stats.pendingEdits}
                </div>
              </div>
            </div>
          )}

          <div style={{
            padding: '16px',
            backgroundColor: '#eff6ff',
            borderRadius: '8px',
            border: '1px solid #3b82f6'
          }}>
            <p style={{ fontSize: '13px', color: '#1e40af', margin: 0 }}>
              💡 Gagnez des points en contribuant à l'amélioration des fiches boutiques. Vos modifications seront validées par les administrateurs.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;